import { m as motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef, type ReactNode } from "react";
import { KineticHeading } from "./KineticHeading";
import { GrainOverlay } from "./GrainOverlay";
import { cn } from "@/lib/utils";

interface Props {
  eyebrow: string;
  title: string;
  /** word indices (0-based) to highlight with the accent style */
  highlight?: number[];
  lede?: string;
  tone?: "mint" | "peach" | "butter";
  meta?: ReactNode;
  children?: ReactNode;
  align?: "left" | "center";
}

const toneClasses = {
  mint: { eyebrow: "text-mint", accent: "text-mint italic font-serif font-normal" },
  peach: { eyebrow: "text-peach", accent: "text-peach italic font-serif font-normal" },
  butter: { eyebrow: "text-butter", accent: "text-butter italic font-serif font-normal" },
} as const;

/** Cinematic editorial-style hero — kinetic heading, soft gradient, scroll parallax. */
export function EditorialHero({
  eyebrow,
  title,
  highlight = [],
  lede,
  tone = "mint",
  meta,
  children,
  align = "left",
}: Props) {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.9], [1, 0]);
  const t = toneClasses[tone];

  return (
    <section
      ref={ref}
      className="relative isolate overflow-hidden pt-40 pb-20 md:pt-52 md:pb-28"
    >
      <motion.div
        style={{ y }}
        className="pointer-events-none absolute inset-0 -z-10"
        aria-hidden
      >
        <div
          className={cn(
            "absolute inset-0 bg-[radial-gradient(ellipse_at_top,var(--tw-gradient-stops))] opacity-60",
            tone === "mint" && "from-mint/20 via-transparent to-transparent",
            tone === "peach" && "from-peach/20 via-transparent to-transparent",
            tone === "butter" && "from-butter/20 via-transparent to-transparent",
          )}
        />
        <GrainOverlay opacity={0.05} />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className={cn("container-wide", align === "center" && "text-center")}
      >
        <div className={cn("max-w-4xl", align === "center" && "mx-auto")}>
          <div className={cn("mb-5 text-xs uppercase tracking-[0.22em]", t.eyebrow)}>
            {eyebrow}
          </div>
          <KineticHeading
            text={title}
            className="text-balance text-5xl font-semibold leading-[1.0] tracking-[-0.03em] text-foreground md:text-7xl lg:text-[88px]"
            highlight={highlight.length ? { indices: highlight, className: t.accent } : undefined}
          />
          {lede && (
            <p
              style={{ animationDelay: "0.5s" }}
              className={cn(
                "animate-kinetic-fade-up mt-8 max-w-2xl text-pretty text-lg leading-relaxed text-foreground/85 md:text-xl",
                align === "center" && "mx-auto",
              )}
            >
              {lede}
            </p>
          )}
          {meta && (
            <div style={{ animationDelay: "0.7s" }} className="animate-kinetic-fade-up mt-10">
              {meta}
            </div>
          )}
          {children}
        </div>
      </motion.div>
    </section>
  );
}
